// components/Navbar.js
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiMenu,
  FiUser,
  FiChevronDown,
  FiLogOut,
  FiSettings,
  FiSearch,
  FiExternalLink,
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";

const Navbar = ({ toggleSidebar, isMobile }) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState("");
  const userMenuRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      clearInterval(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("login2");
    localStorage.removeItem("userId");
    navigate("/login");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 mb-5">
      <div className="bg-gradient-to-r from-blue-950/70 via-indigo-950/70 to-slate-900/70 backdrop-blur-xl border-b border-blue-400/20 shadow-lg shadow-blue-500/5">
        <div className="flex items-center justify-between px-4 md:px-6 py-3">
          {/* Left section */}
          <div className="flex items-center gap-4">
            {isMobile && (
              <motion.button
                onClick={toggleSidebar}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 rounded-xl bg-blue-500/10 border border-blue-400/20 text-blue-300 hover:bg-blue-500/20 transition-colors"
                aria-label="Menu"
              >
                <FiMenu size={20} />
              </motion.button>
            )}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-400/20 group-hover:scale-110 transition-transform">
                <FiExternalLink className="text-blue-400 text-lg" />
              </div>
              <motion.h1
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-lg md:text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent hidden sm:block"
              >
                RefisC-RDC
              </motion.h1>
            </Link>

            {/* Search bar (desktop) */}
           
          </div>

          {/* Right section */}
          <div className="flex items-center gap-3">
            {/* Horloge (cachée sur mobile) */}
            <div className="hidden md:flex flex-col items-end mr-2">
              <span className="text-sm font-mono text-slate-300">
                {currentTime.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
              <span className="text-[10px] text-slate-500">
                {currentTime.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
              </span>
            </div>

            {/* Profil utilisateur */}
            <div className="relative" ref={userMenuRef}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 p-2 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] transition-all"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <FiUser size={16} className="text-white" />
                </div>
                <div className="hidden md:flex flex-col items-start">
                  <span className="text-sm font-medium text-white">REFISC</span>
                  <span className="text-[10px] text-slate-400">Administrateur</span>
                </div>
                <FiChevronDown size={14} className="text-slate-400 hidden md:block" />
              </motion.button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-3 w-56 bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border border-blue-400/20 rounded-2xl shadow-2xl shadow-blue-500/10 overflow-hidden z-50"
                  >
                    <div className="px-4 py-4 text-center border-b border-white/[0.06]">
                      <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-2">
                        <FiUser size={24} className="text-white" />
                      </div>
                      <p className="text-white font-semibold text-sm">REFISC</p>
                      <p className="text-xs text-slate-400">Administrateur</p>
                    </div>

                    <div className="py-2">
                      <Link
                        to="/settings"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-slate-300 hover:text-white hover:bg-white/[0.03] transition-colors text-sm"
                      >
                        <FiSettings size={16} />
                        Paramètres
                      </Link>
                    </div>

                    <div className="border-t border-white/[0.06] py-2">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-red-400 hover:text-red-300 hover:bg-white/[0.03] transition-colors text-sm"
                      >
                        <FiLogOut size={16} />
                        Déconnexion
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Barre de recherche mobile */}
      
      </div>
    </header>
  );
};

export default Navbar;